//#region Imports
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { Dictionary } from "lodash";
import { z } from "zod";
//#endregion

export type MarketplaceSearchRequestJSON = z.infer<typeof MarketplaceSearchRequestSchema.MarketplaceSearchRequestZ>;

/**
 * Request fields with no query parameter of their own on the marketplace page: the page, the
 * vendor-rep switch the page never sets, and the fields the page writes under another name.
 */
const fieldsNotOnThePage: ReadonlySet<string> = new Set([
  "from",
  "size",
  "limitToVendorReps",
  "hideCompanies",
  "hideTeams",
  "hideIndividuals",
  "billingRate",
  "minimumBillingRate",
  "maximumBillingRate",
]);

//#region MarketplaceSearchRequestSchema
export default class MarketplaceSearchRequestSchema {
  public static readonly MarketplaceSearchRequestZ = z.object({
    query: z.string().optional(),
    hideCompanies: z.boolean().optional(),
    hideTeams: z.boolean().optional(),
    hideIndividuals: z.boolean().optional(),
    category: z.string().optional(),
    sortBy: z.string().optional(),
    name: z.string().optional(),
    yearEstablished: z.number().nullish(),
    numberOfEmployees: z.number().nullish(),
    contactPersonUserId: z.uuid().optional(),
    description: z.string().optional(),
    address1: z.string().optional(),
    address2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
    website: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    school: z.string().optional(),
    professionalRegistration: z.string().optional(),
    degree: z.string().optional(),
    technicalBackgrounds: z.array(z.string()).optional(),
    practiceAreas: z.array(z.string()).optional(),
    applicationsDrafted: z.number().nullish(),
    allowedApplications: z.number().nullish(),
    barAdmissionYear: z.number().nullish(),
    yearsExperience: z.number().nullish(),
    isOnCall: z.boolean().optional(),
    insuredAmount: z.number().nullish(),
    billingRate: z.string().optional(),
    billingUnit: z.string().optional(),
    minimumBillingRate: z.number().nullish(),
    maximumBillingRate: z.number().nullish(),
    potentialDiversityCandidate: z.boolean().optional(),
    collaborates: z.boolean().optional(),
    additionalInfo: z.string().optional(),
    fluentLanguages: z.array(z.string()).optional(),
    technicalLanguages: z.array(z.string()).optional(),
    regionsRegistered: z.array(z.string()).optional(),
    regionsLicensed: z.array(z.string()).optional(),
    limitToVendorReps: z.boolean().optional(),
    from: z.number().default(0),
    size: z.number().default(10),
  });

  public static validateJSON(json: unknown): MarketplaceSearchRequestJSON {
    return parseOrThrow(MarketplaceSearchRequestSchema.MarketplaceSearchRequestZ, json, {
      logError: true,
      schemaName: "MarketplaceSearchRequestSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValidJSON(json: unknown): json is MarketplaceSearchRequestJSON {
    return MarketplaceSearchRequestSchema.MarketplaceSearchRequestZ.safeParse(json).success;
  }

  public static deserialize(filters?: Dictionary<string>): MarketplaceSearchRequestJSON {
    if (!filters) {
      return MarketplaceSearchRequestSchema.validateJSON({});
    }

    const parseInt = (value: string | undefined): number | null =>
      value ? Number.parseInt(value) : null;

    const parseBool = (value: string | undefined): boolean | undefined =>
      value !== undefined ? value === "true" : undefined;

    const parseArray = (value: string | undefined): string[] | undefined =>
      value ? value.split(",") : undefined;

    // Inclusion is the UI default, so an absent or true include* value does not constrain the
    // request. Only an explicitly disabled toggle becomes a hide* transport field.
    const hideCompanies = filters["includeCompanies"] === "false" ? true : undefined;
    const hideTeams = filters["includeTeams"] === "false" ? true : undefined;
    const hideIndividuals = filters["includeIndividuals"] === "false" ? true : undefined;

    // A range chip serializes both bounds into one comma-separated parameter ("100,500"), but the
    // API takes them as separate minimum and maximum fields.
    const parseRangeBound = (value: string | undefined, bound: 0 | 1): number | null => {
      const parts = value?.split(",");
      if (!parts || parts.length !== 2) return null;
      const parsed = Number.parseFloat(parts[bound]);
      return Number.isFinite(parsed) ? parsed : null;
    };

    //#region Validation
    return MarketplaceSearchRequestSchema.validateJSON({
      query: filters["query"],
      category: filters["category"],
      hideCompanies,
      hideTeams,
      hideIndividuals,
      sortBy: filters["sortBy"],
      name: filters["name"],
      yearEstablished: parseInt(filters["yearEstablished"]),
      numberOfEmployees: parseInt(filters["numberOfEmployees"]),
      contactPersonUserId: filters["contactPersonUserId"],
      description: filters["description"],
      address1: filters["address1"],
      address2: filters["address2"],
      city: filters["city"],
      state: filters["state"],
      postalCode: filters["postalCode"],
      website: filters["website"],
      firstName: filters["firstName"],
      lastName: filters["lastName"],
      school: filters["school"],
      professionalRegistration: filters["professionalRegistration"],
      degree: filters["degree"],
      technicalBackgrounds: filters["technicalBackgrounds"]?.split(","),
      practiceAreas: filters["practiceAreas"]?.split(","),
      applicationsDrafted: parseInt(filters["applicationsDrafted"]),
      allowedApplications: parseInt(filters["allowedApplications"]),
      barAdmissionYear: parseInt(filters["barAdmissionYear"]),
      yearsExperience: parseInt(filters["yearsExperience"]),
      isOnCall: parseBool(filters["isOnCall"]),
      insuredAmount: parseInt(filters["insuredAmount"]),
      billingRate: filters["billingRate"],
      billingUnit: filters["billingUnit"],
      minimumBillingRate: parseRangeBound(filters["billingRate"], 0),
      maximumBillingRate: parseRangeBound(filters["billingRate"], 1),
      potentialDiversityCandidate: parseBool(filters["potentialDiversityCandidate"]),
      collaborates: parseBool(filters["collaborates"]),
      additionalInfo: filters["additionalInfo"],
      fluentLanguages: parseArray(filters["fluentLanguages"]),
      technicalLanguages: parseArray(filters["technicalLanguages"]),
      regionsRegistered: parseArray(filters["regionsRegistered"]),
      regionsLicensed: parseArray(filters["regionsLicensed"]),
      limitToVendorReps: parseBool(filters["limitToVendorReps"]),
    });
  }

  //#endregion

  //#endregion

  //#region URLSearchParams

  public static toSearchParams(request: MarketplaceSearchRequestJSON): URLSearchParams {
    const params = new URLSearchParams();
    // this used to be a massive number of if statements, so just checkthe type and value instead.
    Object.entries(request).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      if (typeof value === 'boolean') {
        if (value) params.append(key, 'true');
      } else if (Array.isArray(value)) {
        // One key per value, which is how ASP.NET binds a list. Joined into one value, two
        // languages reached the API as a single language called "German,English", so any list
        // filter with a second value matched nobody.
        for (const item of value) params.append(key, item);
      } else if (typeof value === 'string') {
        if (value) params.append(key, value);
      } else {
        // number
        params.append(key, value.toString());
      }
    });
    return params;
  }

  /**
   * The marketplace page's query string for a request: the inverse of {@link deserialize}, so a saved
   * search reopens with the filters and sort it was saved with.
   *
   * A list is one comma-separated value, as the page's filter chips write it. A switch is written
   * only when it is on, and a left-out profile type as its `include*` switch turned off. The billing
   * range goes back into the one `billingRate` value its chip owns.
   */
  public static toPageSearchParams(request: MarketplaceSearchRequestJSON): URLSearchParams {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(request)) {
      if (fieldsNotOnThePage.has(key) || value === null || value === undefined || value === "") continue;
      if (value === false) continue;
      if (Array.isArray(value)) {
        if (value.length > 0) params.set(key, value.join(","));
        continue;
      }
      params.set(key, String(value));
    }

    if (request.hideCompanies) params.set("includeCompanies", "false");
    if (request.hideTeams) params.set("includeTeams", "false");
    if (request.hideIndividuals) params.set("includeIndividuals", "false");

    const { minimumBillingRate, maximumBillingRate } = request;
    if (typeof minimumBillingRate === "number" && typeof maximumBillingRate === "number") {
      params.set("billingRate", `${minimumBillingRate},${maximumBillingRate}`);
    }
    return params;
  }

  //#endregion
}
